import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ListingInfo {
  id: string;
  title: string;
  status: string;
  listing_number: string | null;
  broker_email: string;
  created_at: string;
}

export function ListingActivator() {
  const { toast } = useToast();
  const [brokerEmail, setBrokerEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [listings, setListings] = useState<ListingInfo[]>([]);
  const [selectedListing, setSelectedListing] = useState<ListingInfo | null>(null);

  const searchListings = async () => {
    if (!brokerEmail.trim()) {
      toast({
        title: "Input Required",
        description: "Please enter a broker email",
        variant: "destructive",
      });
      return;
    }

    setSearching(true);
    setListings([]);
    setSelectedListing(null);

    try {
      // Find user by email
      const { data: usersData, error: userError } = await supabase.auth.admin.listUsers();
      
      if (userError) throw userError;
      if (!usersData?.users) throw new Error("No users data returned");

      const broker = usersData.users.find((u: any) => u.email === brokerEmail.trim());
      
      if (!broker) {
        toast({
          title: "Not Found",
          description: "No broker found with that email",
          variant: "destructive",
        });
        return;
      }

      // Get all listings for this broker
      const { data: listingsData, error: listingsError } = await supabase
        .from('listings')
        .select('id, title, status, listing_number, created_at, broker_id')
        .eq('broker_id', broker.id)
        .order('created_at', { ascending: false });

      if (listingsError) throw listingsError;

      if (!listingsData || listingsData.length === 0) {
        toast({
          title: "No Listings",
          description: "This broker has no listings",
          variant: "destructive",
        });
        return;
      }

      const listingsWithEmail = listingsData.map(listing => ({
        ...listing,
        broker_email: broker.email || 'Unknown'
      }));

      setListings(listingsWithEmail);

    } catch (error: any) {
      console.error('Error searching listings:', error);
      toast({
        title: "Search Failed",
        description: error.message || "Could not find listings",
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  const activateListing = async () => {
    if (!selectedListing) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-activate-listing', {
        body: { listing_id: selectedListing.id },
      });

      if (error) throw error;

      toast({
        title: "Listing Activated!",
        description: `Listing ${data.listing_number} is now live. ${data.payments_updated} payment(s) marked as completed.`,
      });

      // Refresh listings
      await searchListings();

    } catch (error: any) {
      console.error('Error activating listing:', error);
      toast({
        title: "Activation Failed",
        description: error.message || "Could not activate listing",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manual Listing Activation</CardTitle>
        <CardDescription>
          Manually activate listings when payment verification fails
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="broker-email">Broker Email</Label>
          <div className="flex gap-2">
            <Input
              id="broker-email"
              type="email"
              placeholder="Enter broker email address"
              value={brokerEmail}
              onChange={(e) => setBrokerEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchListings()}
            />
            <Button 
              onClick={searchListings} 
              disabled={searching || !brokerEmail.trim()}
            >
              {searching ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                'Search'
              )}
            </Button>
          </div>
        </div>

        {listings.length > 0 && (
          <div className="space-y-2">
            <Label>Select Listing to Activate</Label>
            <div className="space-y-2">
              {listings.map((listing) => (
                <Alert 
                  key={listing.id}
                  className={`cursor-pointer transition-colors ${
                    selectedListing?.id === listing.id ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => setSelectedListing(listing)}
                >
                  <AlertDescription>
                    <div className="space-y-1 text-sm">
                      <div><strong>Title:</strong> {listing.title}</div>
                      <div><strong>Status:</strong> <span className={
                        listing.status === 'live' ? 'text-green-600' : 
                        listing.status === 'draft' ? 'text-yellow-600' : 'text-gray-600'
                      }>{listing.status}</span></div>
                      <div><strong>Listing Number:</strong> {listing.listing_number || 'Not assigned'}</div>
                      <div><strong>Created:</strong> {new Date(listing.created_at).toLocaleString()}</div>
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </div>
        )}

        {selectedListing && selectedListing.status !== 'live' && (
          <Button 
            onClick={activateListing} 
            disabled={loading}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Activating...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Activate Selected Listing
              </>
            )}
          </Button>
        )}

        {selectedListing && selectedListing.status === 'live' && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              This listing is already live with number {selectedListing.listing_number}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}